import type { CustomerProfile, Row } from '@contract';
import { OpenCase } from '@/components/cases';
import {
  LimitGroupChange,
  Section,
  TabState,
  useTab,
  WalletActions,
} from '@/components/customer-tabs';
import { SendMessage } from '@/components/messages';
import { ProposeChange } from '@/components/propose-change';
import { Alert } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useStaff } from '@/lib/staff-context';

const RESTRICTION_REASONS = new Set(['emergency', 'risk_review', 'operator']);

/**
 * Everything staff can do to this customer, in one place: restrict or lift a
 * wallet, lift a money-out hold, move them to another limit group, remove a
 * bound phone, open a case or write to them. Most are change requests for a
 * second person to approve; the super admin's apply at once, each with a reason
 * in the audit log.
 */
export function ActionsTab({
  customerId,
  profile,
}: Readonly<{ customerId: string; profile: CustomerProfile }>) {
  const staff = useStaff();
  const wallets = useTab<Row[]>(customerId, 'wallets');
  const devices = useTab<{ devices: Row[] }>(customerId, 'devices');
  const hold = profile.money_out_hold;
  const openDevices = (devices.data?.devices ?? []).filter(
    (device) => !device.revoked_at,
  );

  return (
    <div className="flex flex-col gap-6">
      <Alert variant="info">
        {staff.is_super_admin
          ? 'You own these: each applies at once, with a reason in the audit log. Refunds and corrections stay with Finance and need a second person.'
          : 'Each of these is sent for a second person to approve before anything changes.'}
      </Alert>
      <TabState isPending={wallets.isPending} error={wallets.error} />

      <Section title="Wallets">
        <div className="flex flex-col gap-4">
          {wallets.data?.length === 0 ? (
            <p className="text-sm text-muted-foreground">No wallets.</p>
          ) : null}
          {wallets.data?.map((wallet) => (
            <WalletActionCard key={String(wallet.id)} wallet={wallet} />
          ))}
        </div>
      </Section>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Money-out hold</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 text-sm">
            {hold?.active ? (
              <>
                <p>
                  A hold stops this customer sending money out until it ends.
                </p>
                <div>
                  <ProposeChange
                    kind="lift_hold"
                    payload={{ customer_id: profile.id }}
                    label="Lift early"
                    title="Lift this money-out hold early"
                    description="The customer can send money out again at once."
                  />
                </div>
              </>
            ) : (
              <p className="text-muted-foreground">No hold in place.</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Limit group</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 text-sm">
            <p>
              Now in the{' '}
              <span className="font-medium">
                {profile.limit_group ?? 'default'}
              </span>{' '}
              group.
            </p>
            <div>
              <LimitGroupChange
                customerId={profile.id}
                current={profile.limit_group}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <Section title="Bound phones">
        {openDevices.length === 0 ? (
          <p className="text-sm text-muted-foreground">No bound phones.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {openDevices.map((device) => (
              <li
                key={String(device.id)}
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-surface p-3 text-sm"
              >
                <span>
                  <span className="font-medium">
                    {String(device.model ?? 'Phone')}
                  </span>{' '}
                  <span className="text-muted-foreground">
                    {String(device.platform ?? '')} · last seen{' '}
                    {String(device.last_seen_at ?? 'never').slice(0, 16)}
                  </span>
                </span>
                <ProposeChange
                  kind="revoke_device"
                  payload={{ device_id: String(device.id) }}
                  label="Remove"
                  title="Remove this bound phone"
                  description="The phone is signed out and must be bound again before it can move money."
                />
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Support">
        <div className="flex flex-wrap gap-2">
          <OpenCase customerId={customerId} />
          <SendMessage customerId={customerId} />
        </div>
      </Section>
    </div>
  );
}

/** One wallet: its own actions, and any restriction in force with a way to lift it. */
function WalletActionCard({ wallet }: Readonly<{ wallet: Row }>) {
  const restrictions = ((wallet.restrictions as Row[]) ?? []).filter(
    (row) => !row.lifted_at && RESTRICTION_REASONS.has(String(row.reason)),
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {String(wallet.name)} · {String(wallet.currency)}
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {String(wallet.kind)} wallet {String(wallet.wallet_number)},{' '}
          {String(wallet.status)}
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {wallet.status === 'closed' ? (
          <p className="text-sm text-muted-foreground">Closed.</p>
        ) : (
          <WalletActions wallet={wallet} />
        )}
        {restrictions.map((row) => (
          <div
            key={`${String(row.reason)}-${String(row.set_at)}`}
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-surface-2/50 p-3 text-sm"
          >
            <span>
              In force: {text(row.kind, 'restriction').replaceAll('_', ' ')} (
              {String(row.reason).replaceAll('_', ' ')}) since{' '}
              {text(row.set_at, '').slice(0, 16)}
            </span>
            <ProposeChange
              kind="lift_restriction"
              payload={{
                wallet_id: String(wallet.id),
                reason: String(row.reason),
              }}
              label={row.reason === 'emergency' ? 'Unfreeze' : 'Lift'}
              title={
                row.reason === 'emergency'
                  ? 'Unfreeze this wallet'
                  : 'Lift this restriction'
              }
              description="Money moves again."
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/** A row value as text: strings as they are, anything else (or nothing) as the fallback. */
function text(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}
